"use client";
import { LoadingSmall } from "@/app/components/Loading";
import PartnerPerspectivesSection from "@/app/components/PartnerPerspectivesSection";
import ProTag from "@/app/components/ProTag";
import ReportCard from "@/app/components/ReportCard";
import { useResponsiveImageUrl } from "@/lib/getImageUrl";
import { Box, Container, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

interface ResearchContentProps {
  initialReports: any[];
  initialIndicatorReports: any[];
  initialPartnerReports: any[];
}

export default function ResearchContent({
  initialReports,
  initialIndicatorReports,
  initialPartnerReports,
}: ResearchContentProps) {
  const t = useTranslations("Research");
  const router = useRouter();
  const [reports] = useState(initialReports);
  const [indicatorReports] = useState(initialIndicatorReports);
  const [partnerReports] = useState(initialPartnerReports);
  const [currentPage, setCurrentPage] = useState(0);
  const [contentHeight, setContentHeight] = useState("auto");
  const contentRef = useRef<HTMLDivElement>(null);

  const coverImageUrl = useResponsiveImageUrl("research-cover.png", true);

  const cryptoRows = [];
  for (let i = 0; i < reports.length; i += 2) {
    cryptoRows.push(reports.slice(i, i + 2));
  }

  const handleReportClick = (url: string) => {
    router.push(`/research/reports/${url}`);
  };

  // 响应式布局的每行项目数
  const itemsPerRow = typeof window !== "undefined" && window.innerWidth <= 900 ? 1 : 3;

  // 为Market Intelligence区分页计算页数，在小屏幕上每页显示3项，大屏幕显示9项
  const itemsPerPage = itemsPerRow * 3;
  const miPages = Math.ceil(indicatorReports.length / itemsPerPage);

  // 显示特定页的数据，根据屏幕大小调整每行的项目数
  const showPageItems = (page: number) => {
    const startIdx = page * itemsPerPage;
    const items = indicatorReports.slice(startIdx, startIdx + itemsPerPage);
    const pageRows = [];
    for (let i = 0; i < items.length; i += itemsPerRow) {
      pageRows.push(items.slice(i, i + itemsPerRow));
    }
    return pageRows;
  };

  // 监听窗口大小变化，重新计算布局
  useEffect(() => {
    const handleResize = () => {
      setCurrentPage(0);
      if (contentRef.current) {
        setContentHeight(`${contentRef.current.scrollHeight}px`);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 处理MI区域分页切换
  const handleMIPageChange = (page: number) => {
    if (contentRef.current) {
      setContentHeight(`${contentRef.current.scrollHeight}px`);
    }
    setCurrentPage(page);
    setTimeout(() => {
      if (contentRef.current) {
        setContentHeight(`${contentRef.current.scrollHeight}px`);
      }
    }, 50);
  };

  const currentPageRows = showPageItems(currentPage);

  return (
    <Container maxWidth="lg">
      <Box
        component="img"
        src={coverImageUrl}
        alt="cover"
        sx={{
          width: { xs: "100%", md: "83%" },
          height: "auto",
          mx: "auto",
        }}
      />
      <Box sx={{ mt: 8, mb: { xs: 4, sm: 5 } }}>
        <Typography variant="h4" sx={{ color: "text.main", fontWeight: 600, fontSize: "var(--font-size-3xl)" }}>
          {t("cryptoGiantsTitle")}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 1, fontSize: "var(--font-size-s)" }}>
          {t("cryptoGiantsDescription")}
        </Typography>
      </Box>
      <Box>
        {cryptoRows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              gap: {xs: "1.5rem", sm: "2.5rem"},
              mb: {xs: "1.5rem", sm: "2.5rem"},
            }}>
            {row.map((report, index) => (
              <ReportCard key={index} report={report} onReportClick={handleReportClick} />
            ))}
          </Box>
        ))}
      </Box>
      {/* Market Intelligence 区块 */}
      <Box sx={{ mt: "10rem", mb: 5.5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 5, md: 8 },
            alignItems: { xs: "center", md: "center" },
            justifyContent: { xs: "center", md: "space-between" },
            mb: 6,
          }}>
          <Box
            sx={{
              maxWidth: { xs: "100%", md: "30rem" },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-end" },
              justifyContent: "center",
              order: { xs: 0, md: 1 },
              textAlign: { xs: "center", md: "right" },
              px: { xs: 2, md: 0 },
            }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: "var(--font-size-3xl)",
                fontWeight: 600,
                mb: 1.5,
              }}>
              {t("marketIntelligence.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", fontSize: "1rem" }}>
              {t("marketIntelligence.description")}
            </Typography>
          </Box>

          <Box
            component="img"
            src="/images/research-market-intel.png"
            sx={{
              width: { xs: "100%", sm: "80%", md: "50%" },
              height: "auto",
              objectFit: "cover",
              borderRadius: 1.5,
              order: { xs: 1, md: 0 },
            }}></Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            mb: 3.5,
            height: contentHeight,
            transition: "height 0.3s ease",
            overflow: "hidden",
          }}>
          <Box ref={contentRef}>
            {currentPageRows.map((row, rowIdx) => (
              <Box
                key={rowIdx}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  mb: 3,
                  gap: { xs: 2, md: 5 },
                }}>
                {row.map((report, colIdx) => (
                  <Box
                    key={colIdx}
                    sx={{
                      flex: "1 1 0",
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      mx: { xs: 0, md: colIdx === 1 ? 4 : 0 },
                      display: "flex",
                      alignItems: "flex-end",
                      width: "100%",
                    }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        whiteSpace: "normal",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        transition: "color 0.2s",
                        "&:hover": { color: "primary.main" },
                        width: "100%",
                      }}
                      onClick={() => handleReportClick(report.url)}>
                      {report.isFree || (
                        <ProTag sx={{ display: "inline-block", mr: 1, position: "relative", top: "-2px" }} />
                      )}
                      {report.title}
                    </Typography>
                  </Box>
                ))}
                {itemsPerRow > 1 &&
                  Array.from({ length: Math.max(0, itemsPerRow - row.length) }).map((_, i) => (
                    <Box
                      key={`empty-${i}`}
                      sx={{
                        flex: "1 1 0",
                        px: 2,
                        py: 2,
                        display: { xs: "none", md: "block" },
                      }}
                    />
                  ))}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1 }}>
          {Array.from({ length: miPages }).map((_, idx) => (
            <Box
              key={idx}
              onClick={() => handleMIPageChange(idx)}
              sx={{
                py: 1.5,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Box
                sx={{
                  width: "2.5rem",
                  height: "0.25rem",
                  borderRadius: 0.5,
                  bgcolor: idx === currentPage ? "text.primary" : "text.tertiary",
                  opacity: idx === currentPage ? 1 : 0.5,
                  transition: "all 0.2s",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {/* Partner Perspectives 区块 */}
      <PartnerPerspectivesSection partnerReports={partnerReports} onReportClick={handleReportClick} />
    </Container>
  );
}
